import { Container, Input } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';
import { PiVinylRecord } from 'react-icons/pi';
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { useGetTrackEarnings } from 'services/statistics/track';
import { IStatisticsQuery } from 'services/statistics/types';
import { ITrackMutationForm } from 'services/tracks/types';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';

const TrackStatistics = () => {
  usePermissionGuard('StatisticsPermissionsLIST**');

  const {
    values,
    status,
    setFieldValue,
  }: IFormikContextType<ITrackMutationForm> =
    useFormikContext<ITrackMutationForm>();
  const settlementPeriod = useGetSettlementLightList();

  const trackId = useCustomParamId('id');

  const earnings = useGetTrackEarnings(
    {
      ...values.statistics,
      trackId: trackId,
    },
    !!values.statistics.endSettlementPeriodId &&
      !!values.statistics.startSettlementPeriodId
  );

  const options =
    settlementPeriod.data?.map(p => ({
      label: getSettlementPeriodName(p.settlementPeriodName),
      value: p.id,
    })) ?? [];

  const mappedEarnings = useMemo(() => {
    return earnings.data
      ?.map(e => ({
        name:
          getSettlementPeriodName(
            settlementPeriod.data?.find(s => s.id === e.settlementPeriodId)
              ?.settlementPeriodName
          ) ?? '',
        value: e.earnings ?? 0,
      }))
      .reverse();
    // const startSettlementPeriodIndex = settlementPeriod.data?.findIndex(
    //   p => p.id === values.statistics.startSettlementPeriodId
    // );
    // const endSettlementPeriodIndex = settlementPeriod.data?.findIndex(
    //   p => p.id === values.statistics.endSettlementPeriodId
    // );

    // if (
    //   endSettlementPeriodIndex === undefined ||
    //   startSettlementPeriodIndex === undefined
    // )
    //   return [];

    // return (
    //   settlementPeriod.data
    //     ?.slice(endSettlementPeriodIndex, startSettlementPeriodIndex + 1)
    //     .map(p => ({
    //       name: getSettlementPeriodName(p.settlementPeriodName) ?? '',
    //       value:
    //         earnings.data?.find(e => e.settlementPeriodId === p.id)?.earnings ??
    //         0,
    //     }))
    //     .reverse() ?? []
    // );
  }, [
    earnings.data,
    settlementPeriod.data,
    values.statistics.startSettlementPeriodId,
    values.statistics.endSettlementPeriodId,
    values.mainTitle.title,
  ]);

  useEffect(() => {
    if (settlementPeriod.data) {
      const end = settlementPeriod.data[0];
      const start = settlementPeriod.data[settlementPeriod.data.length - 1];

      const query: IStatisticsQuery = {
        endSettlementPeriodId: end.id,
        startSettlementPeriodId: start.id,
      };

      setFieldValue('statistics', query);
    }
  }, [settlementPeriod.data, values.mainTitle.title]);

  return (
    <div className='flex gap-4 w-full flex-row-reverse'>
      <Container className='min-w-full'>
        <Container.Header
          title={
            <div className=' flex w-full justify-between'>
              <div className='flex gap-2'>
                <PiVinylRecord /> {t('statistics.name')}
              </div>
            </div>
          }>
          <div className='info-box-items flex p-4 w-full min-h-0 min-w-96'>
            <Input.ComplexWrapper className='w-full'>
              <FormInput.Select
                label={t('input.label.start')}
                name='statistics.startSettlementPeriodId'
                placeholder=''
                options={options}
                isClearable={false}
                displayMode={false}
              />
              <FormInput.Select
                label={t('input.label.end')}
                name='statistics.endSettlementPeriodId'
                placeholder=''
                options={options}
                isClearable={false}
                displayMode={false}
              />
            </Input.ComplexWrapper>
          </div>
        </Container.Header>
        <Container.Content>
          <ResponsiveContainer width={'100%'} height={370}>
            <LineChart
              data={mappedEarnings}
              margin={{
                top: 20,
                right: 100,
                bottom: 20,
              }}>
              <CartesianGrid
                strokeDasharray=''
                vertical={false}
                opacity={0.5}
              />
              <XAxis dataKey='name' stroke='var(--secondary-light)' />
              <YAxis
                width={150}
                stroke='var(--secondary-light)'
                tickFormatter={(value: any, index: number) =>
                  value === 0 ? '' : formatCurrency(parseFloat(`${value}`))
                }
                tickCount={5}
              />
              <Tooltip
                cursor={{ fill: 'none' }}
                content={props => (
                  <div
                    className='flex  bg-opacity-95 rounded-sm p-8 gap-3'
                    style={{ backgroundColor: 'var(--background)' }}>
                    <p>{`${props.label}`}</p>
                    {props.payload && props.payload[0] && (
                      <>
                        <p>-</p>
                        <p style={{ color: props.payload[0].color }}>
                          {formatCurrency(
                            parseFloat(`${props.payload[0].value}`)
                          )}
                        </p>
                      </>
                    )}
                  </div>
                )}
              />
              <ReferenceLine y={0} stroke='var(--secondary-light)' />
              <Brush dataKey='name' height={20} stroke='var(--primary)' />
              <Line
                type='monotone'
                dataKey='value'
                fill='var(--primary)'
                stroke='var(--primary)'
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Container.Content>
      </Container>
    </div>
  );
};

export default TrackStatistics;
